import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { EApiMiddlewareMethods } from "../../network/networkHandler.types";
import { OnBoardingState } from "./onBoardingSlice.types";
import AppConstants from "../../constants";
import customNetworHandler from "../../network/customNetworHandler";
import { transformSelectValues } from "../../utils/helpers.utils";
import { getCountryUrlByKey } from "utils/urlUtils";
import { COUNTRY_URL_KEYS } from "constants/CommonConstants";

// ── Shared helpers ─────────────────────────────────────────────────────────

/** Sends a request through the FF_UM country-scoped service. */
const callFfUmService = async (request: Parameters<typeof customNetworHandler>[0]) => {
  const url = getCountryUrlByKey(COUNTRY_URL_KEYS.FF_UM) || "";
  const serviceType = url ? COUNTRY_URL_KEYS.FF_UM : undefined;
  const { data } = await customNetworHandler(request, url, serviceType);
  return data;
};

/** Writes standard API error fields onto a slice state draft. */
const applyApiError = (state: Pick<OnBoardingState, "errorCode" | "error">, payload: any) => {
  state.errorCode = payload?.error ?? AppConstants.RESPONSE_CONSTANTS.ERROR_CODES.DEFAULT_API_FAILED;
  state.error = payload?.message ?? AppConstants.RESPONSE_CONSTANTS.DEFAULT_API_FAILED_ERROR_MSG;
};

// ── Thunks ─────────────────────────────────────────────────────────────────

export const fetchPortalFilter = createAsyncThunk(
  "onBoarding/fetchPortalFilter",
  async ({ payload }: { payload: Record<string, any> }, { rejectWithValue }) => {
    try {
      return await callFfUmService({
        url: "/v1/portal/filter",
        method: EApiMiddlewareMethods.POST,
        data: payload,
      });
    } catch (error: any) {
      return rejectWithValue(error.response.data);
    }
  }
);

export const rejectDriver = createAsyncThunk(
  "onBoarding/rejectDriver",
  async (
    { userId, rejectedFields }: { userId: string; rejectedFields: { field: string; rejectionReason: string }[] },
    { rejectWithValue }
  ) => {
    try {
      return await callFfUmService({
        url: "/v1/users/reject",
        method: EApiMiddlewareMethods.POST,
        data: { rejectedFields },
        headers: { driverId: userId },
      });
    } catch (error: any) {
      return rejectWithValue(error.response?.data);
    }
  }
);

export const approveDriver = createAsyncThunk(
  "onBoarding/approveDriver",
  async ({ userId }: { userId: string }, { rejectWithValue }) => {
    try {
      return await callFfUmService({
        url: "/v1/users/approve",
        method: EApiMiddlewareMethods.POST,
        headers: { driverId: userId },
      });
    } catch (error: any) {
      return rejectWithValue(error.response?.data);
    }
  }
);

export const fetchDocumentSasUrl = createAsyncThunk(
  "onBoarding/fetchDocumentSasUrl",
  async ({ imageUrl }: { imageUrl: string }, { rejectWithValue }) => {
    try {
      return await callFfUmService({
        url: "/api/v1/user-forms/document/sas-url",
        method: EApiMiddlewareMethods.POST,
        data: { imageUrl },
      });
    } catch (error: any) {
      return rejectWithValue(error.response?.data);
    }
  }
);

export const fetchDriverSections = createAsyncThunk(
  "onBoarding/fetchDriverSections",
  async ({ userId }: { userId: string }, { rejectWithValue }) => {
    try {
      return await callFfUmService({
        url: `/api/v1/user-forms/sections?userId=${userId}&flowId=driver_onboarding`,
        method: EApiMiddlewareMethods.POST,
      });
    } catch (error: any) {
      return rejectWithValue(error.response.data);
    }
  }
);

// ── Initial state ──────────────────────────────────────────────────────────

const initialState: OnBoardingState = {
  loading: false,
  errorCode: "",
  error: "",
  driverList: {
    elements: [],
    pageNumber: 0,
    pageSize: 20,
    totalElements: 0,
    totalPages: 0,
    filters: {},
  },
  driverSections: [],
  sectionsLoading: false,
  rejectLoading: false,
  approveLoading: false,
  driverFiltersDropdowns: {
    statusList: [],
    nationalityList: [],
    vehicleTypeList: [],
  },
};

// ── Slice ──────────────────────────────────────────────────────────────────

const onBoardingSlice = createSlice({
  name: "onBoarding",
  initialState,
  reducers: {},
  extraReducers: (builders) => {
    builders
      // fetchPortalFilter
      .addCase(fetchPortalFilter.pending, (state) => {
        state.loading = true;
        state.errorCode = "";
        state.error = "";
      })
      .addCase(fetchPortalFilter.fulfilled, (state, action) => {
        state.loading = false;
        state.errorCode = "";
        state.error = "";
        const results = action.payload?.results;
        if (results) {
          state.driverList = {
            elements:
              (results.pageNumber ?? 0) === 0
                ? (results.elements ?? [])
                : [...state.driverList.elements, ...(results.elements ?? [])],
            pageNumber: results.pageNumber ?? 0,
            pageSize: results.pageSize ?? 20,
            totalElements: results.totalElements ?? 0,
            totalPages: results.totalPages ?? 0,
            filters: results.filters || {},
          };
          if (results.filters) {
            state.driverFiltersDropdowns = {
              statusList: transformSelectValues(results.filters.status || []),
              nationalityList: transformSelectValues(results.filters.nationality || []),
              vehicleTypeList: transformSelectValues(results.filters.vehicleType || []),
            };
          }
        }
      })
      .addCase(fetchPortalFilter.rejected, (state, action) => {
        state.loading = false;
        applyApiError(state, action.payload);
      })

      // fetchDriverSections
      .addCase(fetchDriverSections.pending, (state) => {
        state.sectionsLoading = true;
      })
      .addCase(fetchDriverSections.fulfilled, (state, action) => {
        state.sectionsLoading = false;
        state.driverSections = action.payload?.results || [];
      })
      .addCase(fetchDriverSections.rejected, (state, action) => {
        state.sectionsLoading = false;
        applyApiError(state, action.payload);
      })

      // rejectDriver
      .addCase(rejectDriver.pending, (state) => { state.rejectLoading = true; })
      .addCase(rejectDriver.fulfilled, (state) => { state.rejectLoading = false; })
      .addCase(rejectDriver.rejected, (state) => { state.rejectLoading = false; })

      // approveDriver
      .addCase(approveDriver.pending, (state) => { state.approveLoading = true; })
      .addCase(approveDriver.fulfilled, (state) => { state.approveLoading = false; })
      .addCase(approveDriver.rejected, (state) => { state.approveLoading = false; });
  },
});

export default onBoardingSlice.reducer;
